import React from 'react';
import {
  Box,
  Alert,
  Button,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ExitToApp as ExitIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { ImpersonationService, ImpersonationStatus } from '../../services/ImpersonationService';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';

interface ImpersonationBannerProps {
  status: ImpersonationStatus | null;
  onStopImpersonation: () => void;
}

export const ImpersonationBanner: React.FC<ImpersonationBannerProps> = ({
  status,
  onStopImpersonation,
}) => {
  const navigate = useNavigate();
  const { success, error } = useNotifications();
  const [loading, setLoading] = React.useState(false);

  if (!status || !status.impersonating) {
    return null;
  }

  const handleStopImpersonation = async () => {
    setLoading(true);
    try {
      const response = await ImpersonationService.stopImpersonation();
      
      if (response.success) {
        success('Stopped impersonating. Returning to admin dashboard.');
        onStopImpersonation();
        
        // Navigate to admin dashboard
        navigate('/admin/dashboard');
        
        // Force refresh to update all components
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    } catch (err: any) {
      console.error('Failed to stop impersonation:', err);
      error(
        err.response?.data?.message || 
        'Failed to stop impersonation. Please try again.',
        'Impersonation Error'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Alert
      severity="warning"
      sx={{
        position: 'fixed',
        top: 64, // Below navbar
        left: 0,
        right: 0,
        zIndex: 1200,
        borderRadius: 0,
        boxShadow: 3,
        py: 1,
      }}
      icon={<PersonIcon />}
      action={
        <Tooltip title="Stop impersonating and return to admin dashboard">
          <Button
            color="inherit"
            size="small"
            startIcon={<ExitIcon />}
            onClick={handleStopImpersonation}
            disabled={loading}
            sx={{ fontWeight: 600 }}
          >
            Stop Impersonating
          </Button>
        </Tooltip>
      }
    >
      <Box display="flex" alignItems="center" gap={2}>
        <Typography variant="body2" fontWeight={600}>
          Impersonation Mode Active
        </Typography>
        {status.user && (
          <>
            <Typography variant="body2" color="text.secondary">
              •
            </Typography>
            <Typography variant="body2">
              Viewing as: <strong>{status.user.name}</strong> ({status.user.email})
            </Typography>
            <Typography variant="body2" color="text.secondary">
              •
            </Typography>
            <Typography variant="body2">
              Role: <strong>{status.user.role}</strong>
            </Typography>
          </>
        )}
        {status.admin && (
          <>
            <Typography variant="body2" color="text.secondary">
              •
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Admin: {status.admin.name}
            </Typography>
          </>
        )}
      </Box>
    </Alert>
  );
};

/**
 * Hook to manage impersonation state across the application
 */
export const useImpersonation = () => {
  const [status, setStatus] = React.useState<ImpersonationStatus | null>(null);
  const [loading, setLoading] = React.useState(true);
  const { user } = useAuth();

  const checkStatus = React.useCallback(async () => {
    // Only check impersonation status for admin users or if there's evidence of active impersonation
    if (!user || user.role !== 'admin') {
      setStatus(null);
      setLoading(false);
      return;
    }

    try {
      const currentStatus = await ImpersonationService.getStatus();
      setStatus(currentStatus);
    } catch (error) {
      console.error('Failed to check impersonation status:', error);
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  React.useEffect(() => {
    checkStatus();
    
    // Check status periodically in case it changes
    const interval = setInterval(checkStatus, 30000); // Every 30 seconds
    
    return () => clearInterval(interval);
  }, [checkStatus]);

  const handleStopImpersonation = React.useCallback(() => {
    setStatus(null);
  }, []);

  return {
    status,
    loading,
    isImpersonating: status?.impersonating || false,
    refreshStatus: checkStatus,
    handleStopImpersonation,
  };
};
